import { sanitizeSalesOrderAllocationForWrite } from '#/modules/crmMgmt/salesOrder/helper/salesOrderAllocationHelpers';
import {
  getSalesOrderAllocationMtrId,
  getSalesOrderAllocationStockId,
  getSalesOrderLineInvAllocations,
  getSalesOrderLineQty,
  getSalesOrderTaxMode,
} from '#/modules/crmMgmt/salesOrder/helper/salesOrderFieldAccessors';
import { discountMultiplierToApiRate } from '#/utils/discountRate';

function emptyToNull(value) {
  if (value == null) {
    return null;
  }
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
}

function clonePlainList(list) {
  if (!Array.isArray(list)) {
    return [];
  }
  return list.map((item) => (
    item && typeof item === 'object' && !Array.isArray(item)
      ? { ...item }
      : item
  ));
}

function mapAllocationsToWritePayload(allocations) {
  if (!Array.isArray(allocations)) {
    return [];
  }
  return allocations
    .filter((allocation) => (
      getSalesOrderAllocationMtrId(allocation)
      && getSalesOrderAllocationStockId(allocation)
    ))
    .map((allocation) => sanitizeSalesOrderAllocationForWrite(allocation));
}

function mapLineToWritePayload(line) {
  const payload = {
    productId: line?.productId ?? line?.product?.id ?? null,
    customerOrderLineId: line?.customerOrderLineId ?? null,
    qty: toNumber(getSalesOrderLineQty(line)),
    unitPrice: line?.unitPrice != null && line.unitPrice !== ''
      ? toNumber(line.unitPrice)
      : null,
    remark: emptyToNull(line?.remark),
    invAllocations: mapAllocationsToWritePayload(
      line?.invAllocations ?? getSalesOrderLineInvAllocations(line),
    ),
    extraItems: clonePlainList(line?.extraItems),
  };
  if (line?.discountRate != null && line.discountRate !== '') {
    const rate = toNumber(line.discountRate);
    if (Number.isFinite(rate)) {
      // 表單折數＝價格倍率；後端 discount_rate＝折抵比例，送出前轉換。空省略。
      payload.discountRate = discountMultiplierToApiRate(rate);
    }
  }
  if (line?.id != null && line.id !== '') {
    payload.id = line.id;
  }
  return payload;
}

function mapManualLineToWritePayload(line) {
  if (!line || typeof line !== 'object') {
    return null;
  }
  const payload = {
    itemName: emptyToNull(line.itemName),
    qty: toNumber(line.qty),
    unitName: emptyToNull(line.unitName),
    unitPrice: line.unitPrice != null && line.unitPrice !== ''
      ? toNumber(line.unitPrice)
      : 0,
    remark: emptyToNull(line.remark),
  };
  if (line.id != null && line.id !== '') {
    payload.id = line.id;
  }
  if (
    line.customerOrderManualLineId != null
    && line.customerOrderManualLineId !== ''
  ) {
    payload.customerOrderManualLineId = line.customerOrderManualLineId;
  }
  return payload;
}

/**
 * PATCH 送可編主檔＋完整 lines[]／manualLines[]（full replacement；僅 DRAFT）。
 * 地址／聯絡送三 ID；不送 shippingAddress／recipient_*／invoiceTitle／taxIdNumber。
 * 不送 amount totals／mtrs／warehouse；allocations 只送 mtrId／stockId／qty。
 * 必含 updatedAt（＝ record.updatedAt）。
 */
export function buildSalesOrderWritePayload(values, {
  updatedAt,
  currencyId,
} = {}) {
  const payload = {
    plannedShipDate: emptyToNull(values?.plannedShipDate),
    billingAddressId: emptyToNull(values?.billingAddressId),
    shippingAddressId: emptyToNull(values?.shippingAddressId),
    shippingContactId: emptyToNull(values?.shippingContactId),
    taxMode: emptyToNull(getSalesOrderTaxMode(values)),
    taxRate: values?.taxRate != null && values.taxRate !== ''
      ? toNumber(values.taxRate)
      : null,
    roundingAdjustment: values?.roundingAdjustment != null && values.roundingAdjustment !== ''
      ? toNumber(values.roundingAdjustment)
      : null,
    issueReason: emptyToNull(values?.issueReason),
    remark: emptyToNull(values?.remark),
    lines: (values?.lines ?? []).map(mapLineToWritePayload),
    manualLines: (values?.manualLines ?? [])
      .map(mapManualLineToWritePayload)
      .filter(Boolean),
    updatedAt: updatedAt ?? null,
  };
  if (currencyId !== undefined) {
    payload.currencyId = currencyId;
  }
  return payload;
}

/**
 * PREPARING line PATCH body：僅 `{ updatedAt, invAllocations[] }`。
 */
export function buildSalesOrderLineAllocationsPayload(line, { updatedAt } = {}) {
  return {
    updatedAt: updatedAt ?? null,
    invAllocations: mapAllocationsToWritePayload(
      line?.invAllocations ?? getSalesOrderLineInvAllocations(line),
    ),
  };
}
